import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Users, Plus, Edit3, Shield, ShieldCheck, UserCheck, UserX,
  Search, RefreshCw, Mail, Key
} from 'lucide-react';
import { toast } from 'sonner';

const ROLES = [
  { value: 'provider', label: 'Provider', desc: 'Doctors, NPs, PAs, Counselors', color: '#2563EB' },
  { value: 'nurse', label: 'Nurse', desc: 'RNs, LPNs, Medical Assistants', color: '#059669' },
  { value: 'front_desk', label: 'Front Desk', desc: 'Scheduling, phones, check-in', color: '#0EA5E9' },
  { value: 'billing', label: 'Billing', desc: 'Coders, billers, referrals', color: '#D97706' },
  { value: 'office_manager', label: 'Office Manager', desc: 'Admin + clinical hybrid', color: '#7C3AED' },
  { value: 'admin', label: 'Admin', desc: 'Clinic administration', color: '#DC2626' },
];

function getRoleInfo(role) {
  return ROLES.find(r => r.value === role) || { label: role, desc: '', color: '#6B7280' };
}

function UserEditDialog({ open, onClose, user, onSave }) {
  const [form, setForm] = useState({
    name: '', email: '', role: 'provider', is_super_user: false, is_active: true, password: '',
  });
  const isNew = !user;

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'provider',
        is_super_user: user.is_super_user || false,
        is_active: user.is_active !== false,
        password: '',
      });
    } else {
      setForm({ name: '', email: '', role: 'provider', is_super_user: false, is_active: true, password: '' });
    }
  }, [user, open]);

  const handleSubmit = () => {
    if (!form.name || !form.email) {
      toast.error('Name and email are required');
      return;
    }
    if (isNew && !form.password) {
      toast.error('Password is required for new users');
      return;
    }
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="user-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>
            {isNew ? 'Add User' : 'Edit User'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isNew ? 'Create a new user account' : `Editing ${user?.name}`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium">Name *</Label>
              <Input className="h-8 text-xs mt-1" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Full name" data-testid="user-name-input" />
            </div>
            <div>
              <Label className="text-xs font-medium">Email *</Label>
              <Input className="h-8 text-xs mt-1" type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="user@clinic.com" data-testid="user-email-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium">Role *</Label>
            <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
              <SelectTrigger className="h-8 text-xs mt-1" data-testid="user-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map(r => (
                  <SelectItem key={r.value} value={r.value} className="text-xs">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: r.color }} />
                      {r.label}
                      <span className="text-muted-foreground text-[10px]">— {r.desc}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs font-medium">
              {isNew ? 'Password *' : 'New Password (leave blank to keep current)'}
            </Label>
            <Input className="h-8 text-xs mt-1" type="password" value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder={isNew ? 'Set password' : 'Leave blank to keep current'}
              data-testid="user-password-input" />
          </div>

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-xs font-medium flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-amber-500" />
                Super User Access
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                Grants access to Visit Metrics, Audit Trail, User Management, and System Settings
              </p>
            </div>
            <Switch
              checked={form.is_super_user}
              onCheckedChange={v => setForm(f => ({ ...f, is_super_user: v }))}
              data-testid="user-super-user-toggle"
            />
          </div>

          {!isNew && (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-xs font-medium">Account Status</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {form.is_active ? 'User can log in' : 'User is deactivated'}
                </p>
              </div>
              <Switch
                checked={form.is_active}
                onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))}
                data-testid="user-active-toggle"
              />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" size="sm" onClick={() => onClose(false)}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} data-testid="user-save-button">
            {isNew ? 'Create User' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editDialog, setEditDialog] = useState({ open: false, user: null });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/users');
      setUsers(res.data.users || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Super User access required');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSave = async (formData) => {
    try {
      if (editDialog.user) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editDialog.user.id}`, payload);
        toast.success('User updated');
      } else {
        await api.post('/users', formData);
        toast.success('User created');
      }
      setEditDialog({ open: false, user: null });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save user');
    }
  };

  const filteredUsers = users.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || u.role?.toLowerCase().includes(s);
  });

  const roleGroups = {};
  filteredUsers.forEach(u => {
    const role = u.role || 'unknown';
    if (!roleGroups[role]) roleGroups[role] = [];
    roleGroups[role].push(u);
  });

  if (loading) return (
    <div className="p-4 sm:p-6 max-w-5xl space-y-4">
      <Skeleton className="h-8 w-48" />
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-16" />)}
      </div>
    </div>
  );

  return (
    <div className="p-4 sm:p-6 max-w-5xl animate-fadeIn" data-testid="user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Users className="w-5 h-5 text-primary" /> User Management
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage staff accounts, roles, and Super User access
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              className="pl-8 h-8 text-xs w-48"
              placeholder="Search users..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="user-search-input"
            />
          </div>
          <Button variant="outline" size="sm" onClick={fetchUsers}>
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" onClick={() => setEditDialog({ open: true, user: null })} data-testid="add-user-button">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <Badge variant="outline" className="text-xs tabular-nums">{users.length} total users</Badge>
        <Badge variant="outline" className="text-xs tabular-nums bg-amber-50 text-amber-700 border-amber-200">
          <ShieldCheck className="w-3 h-3 mr-1" />
          {users.filter(u => u.is_super_user).length} super users
        </Badge>
        <Badge variant="outline" className="text-xs tabular-nums">
          {users.filter(u => u.is_active !== false).length} active
        </Badge>
      </div>

      {/* User List by Role */}
      <div className="space-y-4">
        {ROLES.map(roleInfo => {
          const group = roleGroups[roleInfo.value];
          if (!group || group.length === 0) return null;
          return (
            <Card key={roleInfo.value}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: roleInfo.color }} />
                  {roleInfo.label}
                  <Badge variant="outline" className="text-[10px] ml-1">{group.length}</Badge>
                </CardTitle>
                <CardDescription className="text-xs">{roleInfo.desc}</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {group.map(u => (
                  <div
                    key={u.id}
                    className="flex items-center gap-3 px-4 py-2.5 border-t hover:bg-accent/30 transition-colors cursor-pointer"
                    onClick={() => setEditDialog({ open: true, user: u })}
                    data-testid={`user-row-${u.email?.split('@')[0]}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary shrink-0">
                      {u.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{u.name}</span>
                        {u.is_super_user && (
                          <Badge className="text-[9px] h-4 px-1.5 bg-amber-50 text-amber-700 border-amber-200 gap-0.5">
                            <ShieldCheck className="w-2.5 h-2.5" /> Super User
                          </Badge>
                        )}
                        {u.is_active === false && (
                          <Badge variant="outline" className="text-[9px] h-4 px-1.5 text-red-500 border-red-200">
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                        <Mail className="w-2.5 h-2.5" /> {u.email}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1 shrink-0">
                      <Edit3 className="w-3 h-3" /> Edit
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}

        {/* Catch any roles not in ROLES list */}
        {Object.keys(roleGroups).filter(r => !ROLES.find(ri => ri.value === r)).map(role => {
          const group = roleGroups[role];
          return (
            <Card key={role}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{role} <Badge variant="outline" className="text-[10px] ml-1">{group.length}</Badge></CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {group.map(u => (
                  <div key={u.id} className="flex items-center gap-3 px-4 py-2.5 border-t hover:bg-accent/30 transition-colors cursor-pointer"
                    onClick={() => setEditDialog({ open: true, user: u })}>
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-semibold shrink-0">
                      {u.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium">{u.name}</span>
                      <p className="text-[10px] text-muted-foreground">{u.email}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <UserEditDialog
        open={editDialog.open}
        onClose={(v) => setEditDialog({ ...editDialog, open: v })}
        user={editDialog.user}
        onSave={handleSave}
      />
    </div>
  );
}
